import React, { PureComponent } from 'react'
import { uniqBy, sortBy, cloneDeep, isEqual, isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import Autocomplete from 'react-autocomplete'

import FavoriteIcon from './favoriteIcon/FavoriteIcon'
import {
  fetchShipDetailsFromBackend,
  addFavoriteShip,
  removeFavoriteShip,
  searchFavoriteShips
} from '../../../../js/rest'
import './ShipSearchField.scss'
import { handleFetchError } from '../../../../js/sentry'

//FIXME update library autocomplete, we overwrite this method because of a bug in 1.7.2
//we'll update it once https://github.com/reactjs/react-autocomplete/pull/293 is merged
const { findDOMNode } = require('react-dom')
const scrollIntoView = require('dom-scroll-into-view')
Autocomplete.prototype.maybeScrollItemIntoView = function () {
  if (this.isOpen() && this.state.highlightedIndex !== null) {
    const itemNode = this.refs[`item-${this.state.highlightedIndex}`]
    const menuNode = this.refs.menu
    if (itemNode) {
      scrollIntoView(
        findDOMNode(itemNode), // eslint-disable-line react/no-find-dom-node
        findDOMNode(menuNode), // eslint-disable-line react/no-find-dom-node
        { onlyScrollIfNeeded: true }
      )
    }
  }
}

export default class ShipSearchField extends PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      loading: false,
      searchShipInput: props.ship && props.ship.name ? props.ship.name : '',
      shipSearchResults: []
    }
  }

  static propTypes = {
    ship: PropTypes.object,
    onChangeShip: PropTypes.func.isRequired,
    authToken: PropTypes.string,
    hideMMSI: PropTypes.bool,
    hideIdentifier: PropTypes.bool,
    globalSupport: PropTypes.bool,
    hideShipType: PropTypes.bool,
    hideCategoryHeader: PropTypes.bool,
    filterGpsShips: PropTypes.bool,
    customFetch: PropTypes.func,
    fetchShipDetailsOnSelect: PropTypes.bool,
    customShipFetch: PropTypes.func,
    readOnly: PropTypes.bool,
    limit: PropTypes.number,
    terminalId: PropTypes.string.isRequired
  }

  /**
   * Sets ship prop as default.
   * Also adds isFavorite prop on ship
   */
  setupInitialShip = () => {
    if (isEmpty(this.props.ship) || (!this.props.ship.name && !this.props.ship.mmsi)) {
      this.setState({
        searchShipInput: '',
        shipSearchResults: []
      })
      return
    }

    const { name, mmsi, imo, eni } = this.props.ship
    searchFavoriteShips(this.props.terminalId, name, mmsi, imo, eni, this.props.authToken, this.props.customFetch)
      .then(favShips => {
        // merge response from favShips endpoint - which indicates whether ship is marked as fav on backend
        const favShipResponse = favShips.find(s => s.mmsi === mmsi) || {}
        this.setState({
          searchShipInput: name || mmsi || '',
          shipSearchResults: [{ ...favShipResponse, ...this.props.ship }]
        })
      })
      .catch(handleFetchError)
  }

  componentDidMount = () => {
    this.setupInitialShip()
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(this.props.ship, prevProps.ship)) {
      this.setupInitialShip()
    }
  }

  render() {
    return (
      <Autocomplete
        value={this.state.searchShipInput}
        inputProps={{
          name: 'Ship',
          placeholder: 'Search a ship',
          id: 'ship-search-component-input',
          disabled: this.props.readOnly
        }}
        wrapperProps={{ className: 'ship-search-component' }}
        items={this.state.shipSearchResults}
        getItemValue={item => item.name || item.mmsi}
        onSelect={this.onShipSearchResultSelected}
        onChange={this.onSearchShipInputChange}
        renderItem={this.renderShipSearchResultsListItem}
        renderMenu={this.renderSearchResultsList}
      />
    )
  }

  sortArray = (a, b) => {
    if (a.name === '') return 1
    else if (b.name === '') return -1
    else if (a.name === b.name) return 0
    else return a.name < b.name ? -1 : 1
  }

  tryToFindShip = (shipNameInput, filterGpsShips) => {
    const regex = /\//
    const shipName = shipNameInput.match(regex)
      ? shipNameInput
          .split('')
          .filter(char => !char.match(regex))
          .join('')
      : shipNameInput

    const name = isNaN(shipName) ? shipName : null
    const mmsi = !isNaN(shipName) ? shipName : null
    const imo = !isNaN(shipName) ? shipName : null
    const eni = !isNaN(shipName) ? shipName : null

    this.getShipsByQuery(name, mmsi, imo, eni).then(rawSearchResults => {
      const uniqueResults = uniqBy(rawSearchResults, 'mmsi')
      const sortedResults = sortBy(uniqueResults, 'name')

      const normalisedResults = this.normaliseSearchResults(sortedResults, 'name')
      const filteredResults = filterGpsShips
        ? normalisedResults.filter(ship => {
            return ship.teqplayId == null
          })
        : normalisedResults

      const orderedFilteredResults = this.orderSearchShips(filteredResults)

      this.setState({ shipSearchResults: orderedFilteredResults, loading: false })
    })
  }

  updateSelectedShipByMmsi = async ({ mmsi }, shipName) => {
    try {
      const shipDetails = await fetchShipDetailsFromBackend(mmsi, this.props.authToken, this.props.customFetch)
      const shipLength =
        shipDetails.positionOfTransponder != null
          ? shipDetails.positionOfTransponder.distanceToBow + shipDetails.positionOfTransponder.distanceToStern
          : null
      const shipWidth =
        shipDetails.positionOfTransponder != null
          ? shipDetails.positionOfTransponder.distanceToPort + shipDetails.positionOfTransponder.distanceToStarboard
          : null

      const selectedShip = {
        mmsi: shipDetails.mmsi ? shipDetails.mmsi : null,
        imo: shipDetails.imoNumber ? shipDetails.imoNumber : null,
        length: shipLength ? shipLength : null,
        width: shipWidth ? shipWidth : null,
        name: shipName,
        shipType: shipDetails.shipType ? shipDetails.shipType : null,
        seaShip: !!shipDetails.imoNumber
      }

      this.props.onChangeShip(selectedShip, shipDetails)

      this.setState({
        searchShipInput: shipName ? shipName : shipDetails.mmsi,
        shipSearchResults: [selectedShip]
      })
    } catch (err) {
      handleFetchError(err)
    }
  }

  /**
   * When user selects ship from ship search results
   * @param shipNameFromInput
   * @param ship
   */
  onShipSearchResultSelected = (shipNameFromInput, ship) => {
    if (!ship.mmsi) {
      // ignore invalid selection
      return
    }

    const shipName = ship.name ? this.removeSpecialCharsFromString(ship.name) : null

    if (this.props.fetchShipDetailsOnSelect !== false) {
      // On change fetch selected ship details
      this.updateSelectedShipByMmsi(ship, shipName)
    } else {
      // On change only send ship basics
      const simpleShip = {
        mmsi: ship.mmsi ? ship.mmsi : null,
        imo: ship.imoNumber ? ship.imoNumber : null,
        name: shipName,
        shipType: ship.shipType ? ship.shipType : null,
        seaShip: !!ship.imoNumber
      }

      this.props.onChangeShip(simpleShip)

      this.setState({ searchShipInput: shipName ? shipName : ship.mmsi })
    }
  }

  /**
   * Handle change of the ship search input
   * @param event
   * @param input
   */
  onSearchShipInputChange = (event, input) => {
    this.setState({ searchShipInput: input, loading: true })

    // Fetch search results based on input query
    if (input && input.length >= 2) {
      this.tryToFindShip(input, this.props.filterGpsShips)
    }
  }

  /**
   * Remove special characters from searchresults
   * @param searchResults
   * @param fieldname
   */
  normaliseSearchResults = (searchResults, fieldname) => {
    return searchResults.map(searchResult => {
      const formattedSearchResult = cloneDeep(searchResult)
      formattedSearchResult.name =
        formattedSearchResult.name != null ? this.removeSpecialCharsFromString(searchResult[fieldname]) : ''

      return formattedSearchResult
    })
  }

  /**
   * Updates the isFavorite property for the ship marching the given mmsi in local state
   */
  updateShipFavoriteState(mmsi, favStatus) {
    const { shipSearchResults } = this.state
    const shipIndex = shipSearchResults.findIndex(ship => ship.mmsi === mmsi)

    if (shipIndex > -1) {
      const updatedResults = [...shipSearchResults]
      updatedResults[shipIndex].favorite = favStatus
      this.setState({
        shipSearchResults: [...updatedResults]
      })
    }
  }

  /**
   * Returns a function to handle user click on Favorite icon.
   * Sends a request to the backend, and then updates state with new ship status.
   */
  handleShipFavoriteToggleClick = ship => {
    const isFav = ship.favorite === true

    const apiFn = isFav ? removeFavoriteShip : addFavoriteShip
    apiFn(ship.mmsi, this.props.terminalId, this.props.authToken, this.props.customFetch)
      //toggle current ship.favorite status in state
      .then(() => this.updateShipFavoriteState(ship.mmsi, !isFav))
  }

  /**
   * Render ship search result list item
   * @param item
   * @param isHighlighted
   */
  renderShipSearchResultsListItem = (item, isHighlighted) => {
    const highLightedClassName = isHighlighted ? 'item highlighted' : 'item'

    const hasImoNumber = item.imoNumber && Number(item.imoNumber) !== 0
    const hasEniNumber = item.eni
    const onlyHasMmsi = !hasEniNumber && !hasImoNumber
    const shipType = !(item.type === undefined || item.type === 'UNDEFINED' || item.type === 'NOT_AVAILABLE')
      ? item.type.replace(/_/g, ' ')
      : 'NOT AVAILABLE'

    return (
      <div
        className={highLightedClassName}
        key={item.name + '_' + item.mmsi + item.timeLastUpdate}
        data-ship-name={item.name}
        id={item.mmsi}
      >
        <div className="item-details">
          <div className="row name">
            <span className="value">{item.name !== '' && item.name != null ? item.name : 'MMSI: ' + item.mmsi}</span>
          </div>
          {item.mmsi && item.name != null && item.name !== '' && !this.props.hideMMSI ? (
            <div className="row">
              <span className="value">MMSI: {item.mmsi}</span>
            </div>
          ) : (
            ''
          )}
          {!onlyHasMmsi && !this.props.hideIdentifier ? (
            <div className="row">
              <span className="value">
                {`${hasImoNumber ? 'IMO: ' : 'ENI: '}`}
                {`${hasImoNumber ? item.imoNumber : item.eni}`}
              </span>
            </div>
          ) : null}
          {!this.props.hideShipType ? (
            <div className="row">
              <span className="value">Type: {shipType}</span>
            </div>
          ) : null}
          {item.length && (
            <div className="row">
              <span className="value">Length: {item.length} m</span>
            </div>
          )}
        </div>
        <div className="item-fav-icon">
          <FavoriteIcon
            handleClick={() => this.handleShipFavoriteToggleClick(item)}
            highlighted={item.favorite === true}
            altText={`${item.favorite ? 'Unmark' : 'Mark'} this ship as favorite`}
          />
        </div>
      </div>
    )
  }

  /**
   * Render ship search result list
   * @param items
   * @param value
   */
  renderSearchResultsList = (items, value) => {
    return (
      <div className={'ship-search-field-result-menu'}>
        {value == null || value === '' || value.length < 2 ? (
          <div style={{ padding: 6 }}>Search using a name, mmsi, imo or eni</div>
        ) : this.state.loading ? (
          <div style={{ padding: 6 }}>Loading...</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 6 }}>No ships found for {value}</div>
        ) : (
          this.renderSearchResultsListCategoryHeader(items)
        )}
      </div>
    )
  }

  /**
   * Render category headers in ship search results list
   * @param items
   */
  renderSearchResultsListCategoryHeader = items => {
    return items.map((item, index) => {
      const text = item.props['data-ship-name']
      if (
        (index === 0 || items[index - 1].props['data-ship-name'].charAt(0) !== text.charAt(0)) &&
        !this.props.hideCategoryHeader
      ) {
        return [
          <div key={index} className="item-category-header">
            {text.charAt(0)}
          </div>,
          item
        ]
      } else {
        return item
      }
    })
  }

  /**
   * Removes all special characters from given string
   * @param str
   */
  removeSpecialCharsFromString = str => {
    return str.replace(/[^\w\s]/gi, '')
  }

  /**
   * Get ships by search query
   * @param name
   * @param mmsi
   * @param imo
   * @param eni
   * @returns {Promise}
   */
  getShipsByQuery = async (name, mmsi, imo, eni) => {
    const { authToken, customFetch, terminalId } = this.props

    try {
      // const [favouriteResults = [], knownVessels = []] = await Promise.all([
      //   searchFavoriteShips(terminalId, name, mmsi, imo, eni, authToken, customFetch),
      //   fetchShipsByQueryFromBackend(name, mmsi, imo, eni, true, authToken, customFetch, limit, false)
      // ])

      return await searchFavoriteShips(terminalId, name, mmsi, imo, eni, authToken, customFetch)
    } catch (err) {
      handleFetchError(err)
      return []
    }
  }

  /**
   * Order the ships in -> Favorites, Cargo, Tanker, Ships with no type
   * @param shipSearchResultsUnsorted
   */
  orderSearchShips = shipSearchResultsUnsorted => {
    return sortBy(
      shipSearchResultsUnsorted,
      ship => (ship.favorite === true ? 0 : 1), // favorites first
      ship => (ship.type && ship.type === 'CARGO' ? 0 : 1), // then cargo ships
      ship => (ship.type && ship.type !== 'CARGO' && ship.type.includes('CARGO') ? 0 : 1), // then ships with cargo in type name
      ship => (ship.type && ship.type === 'TANKER' ? 0 : 1), // then tankers
      ship => (ship.type && ship.type !== 'TANKER' && ship.type.includes('TANKER') ? 0 : 1) // then ships with tankers in type name
    )
  }
}
